import { NodeComponentType } from '@cast/types';

import { CostPeriod } from '../_types/cost';
import { NodeWithDependencies } from '../types';

const applyCostPeriod = (hourlyPrice: number, costPeriod: CostPeriod) => {
  switch (costPeriod) {
    case 'hourly':
      return hourlyPrice;
    case 'daily':
      return hourlyPrice * 24;
    case 'monthly':
      return hourlyPrice * 24 * 30;
  }
};

/**
 * Reads the manually configured per-CPU price from the node's CPU pricing
 * component. For CAST AI Anywhere clusters the backend exposes resource-level
 * prices set by the customer, so we display them directly instead of deriving
 * the value from the total node price.
 *
 * Returns the raw per-CPU price string (which may be '0' for a configured
 * $0.000/h price) or null when no CPU component price is available.
 */
const getCpuComponentPrice = (
  node: NodeWithDependencies | undefined,
  priceKind: 'totalPricePerUnit' | 'basePricePerUnit'
): string | null => {
  const cpuComponent = node?.pricingInfo?.components?.find(
    (component) => component.type === NodeComponentType.CPU
  );
  const price = cpuComponent?.price?.[priceKind];

  // Use != null so a configured '0' (e.g. $0.000/h) is honored rather than
  // treated as missing; only undefined/null triggers the fallback.
  return price != null ? price : null;
};

export const calculateCostPerCPU = ({
  node,
  costPeriod,
  isCastAnywhere = false,
}: {
  node?: NodeWithDependencies;
  costPeriod: CostPeriod;
  isCastAnywhere?: boolean;
}) => {
  // For Anywhere clusters, prefer the manually configured per-CPU price when
  // the backend provides it; otherwise fall back to the total-price derivation.
  if (isCastAnywhere) {
    const manualCpuPrice = getCpuComponentPrice(node, 'totalPricePerUnit');
    if (manualCpuPrice != null) {
      return applyCostPeriod(+manualCpuPrice, costPeriod);
    }
  }

  const instancePrice = node?.pricingInfo?.totalPrice || null;
  const milliCpu = node?.resources?.cpuCapacityMilli;
  const initialPrice =
    instancePrice && milliCpu ? +instancePrice / (milliCpu / 1000) : 0;

  return applyCostPeriod(initialPrice, costPeriod);
};

export const calculateBaseCostPerCPU = ({
  node,
  costPeriod,
  isCastAnywhere = false,
}: {
  node?: NodeWithDependencies;
  costPeriod: CostPeriod;
  isCastAnywhere?: boolean;
}) => {
  // For Anywhere clusters, prefer the manually configured per-CPU base price
  // when the backend provides it; otherwise fall back to the derivation.
  if (isCastAnywhere) {
    const manualCpuBasePrice = getCpuComponentPrice(node, 'basePricePerUnit');
    if (manualCpuBasePrice != null) {
      return applyCostPeriod(+manualCpuBasePrice, costPeriod);
    }
  }

  const instanceBasePrice = node?.pricingInfo?.basePrice || null;
  const milliCpu = node?.resources?.cpuCapacityMilli;
  const initialPrice =
    instanceBasePrice && milliCpu ? +instanceBasePrice / (milliCpu / 1000) : 0;

  return applyCostPeriod(initialPrice, costPeriod);
};

export const calculateInstanceCost = ({
  node,
  costPeriod,
}: {
  node?: NodeWithDependencies;
  costPeriod: CostPeriod;
}): number => {
  const instancePrice = node?.pricingInfo?.totalPrice || null;
  const initialPrice = +instancePrice!;
  switch (costPeriod) {
    case 'hourly':
      return initialPrice;
    case 'daily':
      return initialPrice * 24;
    case 'monthly':
      return initialPrice * 24 * 30;
  }
};

export const calculateInstanceRegularCost = ({
  node,
  costPeriod,
}: {
  node?: NodeWithDependencies;
  costPeriod: CostPeriod;
}): number => {
  const instanceRegularPrice = node?.pricingInfo?.totalRegularPrice || null;
  const initialPrice = +instanceRegularPrice!;
  switch (costPeriod) {
    case 'hourly':
      return initialPrice;
    case 'daily':
      return initialPrice * 24;
    case 'monthly':
      return initialPrice * 24 * 30;
  }
};
